import { Field } from 'redux-form';
import { renderInput, renderRichTextEditorMultiLang } from 'src/components/misc/redux-form-helpers';
import React, { useState } from 'react';

export interface LanguageItem {
    code: string; // e.g., 'en', 'de', 'fr'
    name: string; // e.g., 'English', 'Deutsch', 'Français'
}

// noinspection SpellCheckingInspection
export const languages: LanguageItem[] = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'it', name: 'Italiano' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'sv', name: 'Svenska' },
    { code: 'sk', name: 'Slovenčina' },
    { code: 'pl', name: 'Polski' },
    { code: 'cs', name: 'Čeština' },
];

interface MultiLanguageInputProps {
    isReadonly: boolean;
    operatorDefaultLanguage?: string;
    label: string;
    t: any;
    input: any;
    componentType: string;
    isUrlValidation: boolean;
    hintText?: string;
    meta?: any;
    placeholder?: string;
}
const MultiLanguageInput = (props: MultiLanguageInputProps) => {
    const { operatorDefaultLanguage, t, isReadonly, label, input, componentType, placeholder, isUrlValidation } = props;

    const name = input.name;
    const [selectedLanguage, setSelectedLanguage] = useState<string>(
        operatorDefaultLanguage ? operatorDefaultLanguage : 'de',
    );

    const getDropdownLanguages = () => {
        const topLanguagesCodes = [operatorDefaultLanguage, 'de', 'en', 'fr'];
        return languages.filter(lang => !topLanguagesCodes.includes(lang.code));
    };

    const getInitialLanguageOptions = () => {
        const uniqueCodes = new Set<string>();
        const orderedLanguages: typeof languages = [];

        const defaultLang = languages.find(l => l.code === operatorDefaultLanguage);
        if (defaultLang && !uniqueCodes.has(defaultLang.code)) {
            orderedLanguages.push(defaultLang);
            uniqueCodes.add(defaultLang.code);
        }

        ['de', 'en', 'fr'].forEach(code => {
            const lang = languages.find(l => l.code === code);
            if (lang && !uniqueCodes.has(lang.code)) {
                orderedLanguages.push(lang);
                uniqueCodes.add(lang.code);
            }
        });
        return orderedLanguages;
    };

    const initialLanguageOptions = getInitialLanguageOptions();
    const dropdownLanguages = getDropdownLanguages();

    const handleLanguageChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedLanguage(event.target.value);
    };

    const languageSpecificFieldName = `${name}.${selectedLanguage}`;

    const disableSwitchButtons =
        props.meta.error && Object.prototype.hasOwnProperty.call(props.meta.error, selectedLanguage) && isUrlValidation;

    return (
        <>
            <Field
                name={languageSpecificFieldName}
                label={`${label} (${selectedLanguage.toUpperCase()})`}
                t={t}
                hintText={props.hintText}
                component={componentType === 'renderInput' ? renderInput : renderRichTextEditorMultiLang}
                disabled={isReadonly}
                placeholder={`${placeholder} (${selectedLanguage.toUpperCase()})`}
            />

            <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                {' '}
                {initialLanguageOptions.map(lang => (
                    <button
                        key={lang.code}
                        onClick={() => setSelectedLanguage(lang.code)}
                        style={{
                            marginRight: '8px',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            backgroundColor: disableSwitchButtons
                                ? '#e1e1e1'
                                : selectedLanguage === lang.code
                                  ? '#007bff'
                                  : '#f0f0f0',
                            color: disableSwitchButtons
                                ? '#a0a0a0'
                                : selectedLanguage === lang.code
                                  ? 'white'
                                  : 'black',
                            border: disableSwitchButtons === false ? '1px solid #ccc' : 'none',
                            borderRadius: '4px',
                        }}
                        disabled={disableSwitchButtons}
                        type="button">
                        {lang.code.toUpperCase()}
                    </button>
                ))}
                {dropdownLanguages.length > 0 && (
                    <select
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                        disabled={disableSwitchButtons}
                        style={{
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            minWidth: '120px',
                        }}>
                        {dropdownLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                                {lang.code.toUpperCase()}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        </>
    );
};

export default MultiLanguageInput;
